import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { formatRoute } from 'react-router-named-routes';
import Icon from '@mdi/react';
import { useNavigate } from 'react-router-dom';
import { mdiPlusCircle } from '@mdi/js';
import { Menu, MenuItem } from '@mui/material';
import { usePlannerViewWeekStartDate } from './planner_hooks';
import {
  currentViewWeekStartDateStr,
  dbWeekStartDateForViewWeekAndIndex,
  plannerDayCalendarDate,
  calendarDateForViewWeekAndIndex,
} from '../../services/planner';
import { daysSince } from '../../services/programmes';
import PopupTitle from '../common/popup_title';
import {
  END_USER_FIND_ALTERNATIVES_ROUTE,
  END_USER_PLANNER_RECOMMENDER_ROUTE,
} from '../../services/routes';
import { linkWithQueryParams } from '../../services/urls';
import { programmeStartCalendarDay } from '../../services/my_day';
import LogFoodItemContainer from './log_food_item_container';
import { useTracker } from '../../operations/tracking_operations';
import {
  useCurrentProgrammeEnrollmentQuery,
  useGetUserProfileQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';

const AddPlannerCardLink = ({ laneId }) => {
  const navigate = useNavigate();

  const { data: userProfile } = useGetUserProfileQuery();

  const plannerViewWeekStartDate =
    usePlannerViewWeekStartDate() ||
    currentViewWeekStartDateStr(
      1 /* plannerViewWeekStartDayOrDefault(userProfile?.plannerViewWeekStartDay) */,
    );

  const { data: currentProgrammeEnrollment } =
    useCurrentProgrammeEnrollmentQuery();

  const tracker = useTracker();

  const isUserEnrolledIntoProgramme = !!currentProgrammeEnrollment;

  const calendarDay = calendarDateForViewWeekAndIndex(
    plannerViewWeekStartDate,
    laneId,
  );

  const dayIndexStr = laneId;
  const dayIndex = parseInt(dayIndexStr, 10);
  const plannerDbWeekStartDate = dbWeekStartDateForViewWeekAndIndex(
    plannerViewWeekStartDate,
    dayIndex,
  );
  const todaysDate = plannerDayCalendarDate(plannerDbWeekStartDate, dayIndex);
  const programmeStartDay = programmeStartCalendarDay(
    currentProgrammeEnrollment,
  );
  const daysSinceStart = daysSince(todaysDate, programmeStartDay);
  const isOutsideEnrollmentTimePeriod = daysSinceStart < 0; // TODO also should not allow auto planning beyond the limit of the programme?

  const [popupMenuAnchorEl, setPopupMenuAnchorEl] = useState(null);

  const [logFoodItemVisible, setLogFoodItemVisible] = useState(false);

  const handleClosePopupMenu = () => {
    setPopupMenuAnchorEl(null);
  };

  const handleSearchForMeal = () => {
    handleClosePopupMenu();
    navigate(
      linkWithQueryParams(
        formatRoute(END_USER_FIND_ALTERNATIVES_ROUTE, {
          calendarDay,
        }),
        { scrollCookie: new Date().getTime().toString() },
      ),
    );
  };

  const handleAutoPlan = () => {
    navigate(
      formatRoute(END_USER_PLANNER_RECOMMENDER_ROUTE, {
        startDate: plannerViewWeekStartDate,
        dayIndex: dayIndexStr,
      }),
    );
  };

  const handleLogFoodItem = () => {
    tracker(userProfile, ['Log food item clicked']);
    handleClosePopupMenu();
    setLogFoodItemVisible(true);
  };

  const onDismissLogFoodItem = useCallback(() => {
    setLogFoodItemVisible(false);
  }, []);

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid,jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
      <a
        onClick={(ev) => {
          setPopupMenuAnchorEl(ev.target);
        }}
        style={{
          display: 'flex',
          color: 'black',
          padding: '5px 2px',
          textDecoration: 'none',
          fontSize: '16px',
          fontWeight: 'bold',
          cursor: 'pointer',
        }}>
        <Icon
          path={mdiPlusCircle}
          style={{
            width: '32px',
            height: '32px',
            cursor: 'pointer',
            // color: theme.palette.primary.dark,
            // marginTop: 6,
            justifyContent: 'flex-end',
          }}
          onClick={(ev) => {
            setPopupMenuAnchorEl(ev.target);
          }}
        />
        <div style={{ margin: 'auto 0 auto 6px' }}>Add new</div>
      </a>
      {popupMenuAnchorEl && (
        <Menu
          anchorEl={popupMenuAnchorEl}
          open
          onClose={handleClosePopupMenu}
          PaperProps={{
            style: { minWidth: '200px' },
          }}>
          <PopupTitle
            titleText="Add menu"
            closeButtonEnabled
            onClickClose={handleClosePopupMenu}
          />
          <MenuItem onClick={handleSearchForMeal}>Search for a Meal</MenuItem>
          {isUserEnrolledIntoProgramme && !isOutsideEnrollmentTimePeriod && (
            <MenuItem onClick={handleAutoPlan}>Plan meals for me</MenuItem>
          )}
          <MenuItem onClick={handleLogFoodItem}>Log a Food Item</MenuItem>
        </Menu>
      )}
      {logFoodItemVisible && (
        <LogFoodItemContainer
          plannerViewWeekStartDate={plannerViewWeekStartDate}
          dayIndex={dayIndex}
          onDismiss={onDismissLogFoodItem}
        />
      )}
    </>
  );
};

AddPlannerCardLink.propTypes = {
  laneId: PropTypes.string.isRequired,
};

export default AddPlannerCardLink;
